import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const movie = movies.find((m) => m.slug === slug);
  const article = articles.find((a) => a.slug === slug);

  const isMovie = !!movie;
  const content = movie || article;

  if (!content) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="mt-4 text-xl font-heading font-bold text-foreground-800">未找到内容</h1>
          <p className="mt-2 text-sm text-foreground-600">该内容不存在或已被移除</p>
          <Link to="/" className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const pageTitle = isMovie
    ? `${(movie as typeof movies[0]).title} - 奇优影院免费在线观看高清`
    : `${(article as typeof articles[0]).title} - 奇优影院`;
  const pageDesc = isMovie
    ? `奇优影院免费在线观看《${(movie as typeof movies[0]).title}》，${(movie as typeof movies[0]).description.slice(0, 100)}`
    : (article as typeof articles[0]).summary.slice(0, 150);
  const pageKeywords = isMovie
    ? `奇优影院,${(movie as typeof movies[0]).title},免费在线观看,${(movie as typeof movies[0]).genre.join(',')},高清电影`
    : `奇优影院,${(article as typeof articles[0]).tags.join(',')}`;

  const relatedMovies = movies
    .filter((m) => {
      if (isMovie) {
        const mv = movie as typeof movies[0];
        return m.id !== mv.id && m.categorySlug === mv.categorySlug;
      }
      return true;
    })
    .slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`https://www.qiyoucinema.com/article/${slug}`}
        ogType="article"
        articlePublishedTime={isMovie ? (movie as typeof movies[0]).updateInfo : (article as typeof articles[0]).publishDate}
      />
      <SchemaOrg type={isMovie ? 'WebPage' : 'Article'} data={isMovie ? {
        name: pageTitle,
        description: pageDesc,
      } : {
        headline: (article as typeof articles[0]).title,
        description: (article as typeof articles[0]).summary,
        author: { '@type': 'Organization', name: (article as typeof articles[0]).author },
        datePublished: (article as typeof articles[0]).publishDate,
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-[1200px] mx-auto">
              <Breadcrumb items={isMovie ? [
                { label: '首页', path: '/' },
                { label: (movie as typeof movies[0]).category, path: `/category/${(movie as typeof movies[0]).categorySlug}` },
                { label: (movie as typeof movies[0]).title },
              ] : [
                { label: '首页', path: '/' },
                { label: '影视资讯', path: '/category/all' },
                { label: (article as typeof articles[0]).title },
              ]} />
            </div>
          </div>

          {isMovie && (
            <article>
              <div className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
                <img
                  src={(movie as typeof movies[0]).backdrop}
                  alt={(movie as typeof movies[0]).title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent"></div>
              </div>

              <div className="px-4 md:px-6 -mt-32 relative z-10">
                <div className="max-w-[1200px] mx-auto">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-shrink-0 w-40 md:w-56">
                      <img
                        src={(movie as typeof movies[0]).poster}
                        alt={(movie as typeof movies[0]).title}
                        title={`${(movie as typeof movies[0]).title} - 奇优影院免费在线观看`}
                        className="w-full aspect-[3/4] object-cover object-top rounded-lg border border-background-200/70"
                      />
                    </div>

                    <div className="flex-1 pt-4 md:pt-10">
                      <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-800">
                        {(movie as typeof movies[0]).title}
                      </h1>
                      <p className="mt-2 text-xs text-foreground-500">
                        {(movie as typeof movies[0]).updateInfo}
                      </p>

                      <div className="flex flex-wrap items-center gap-2 mt-4">
                        {(movie as typeof movies[0]).genre.map((g) => (
                          <span key={g} className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700">
                            {g}
                          </span>
                        ))}
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
                        <div>
                          <span className="text-xs text-foreground-500">评分</span>
                          <p className="text-sm font-semibold text-primary-500">⭐ {(movie as typeof movies[0]).rating}</p>
                        </div>
                        <div>
                          <span className="text-xs text-foreground-500">时长</span>
                          <p className="text-sm font-semibold text-foreground-700">{(movie as typeof movies[0]).duration}</p>
                        </div>
                        <div>
                          <span className="text-xs text-foreground-500">地区</span>
                          <p className="text-sm font-semibold text-foreground-700">{(movie as typeof movies[0]).region}</p>
                        </div>
                        <div>
                          <span className="text-xs text-foreground-500">年份</span>
                          <p className="text-sm font-semibold text-foreground-700">{(movie as typeof movies[0]).year}</p>
                        </div>
                        <div>
                          <span className="text-xs text-foreground-500">导演</span>
                          <p className="text-sm font-semibold text-foreground-700">{(movie as typeof movies[0]).director}</p>
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                          <span className="text-xs text-foreground-500">主演</span>
                          <p className="text-sm font-semibold text-foreground-700">{(movie as typeof movies[0]).cast.join(' / ')}</p>
                        </div>
                      </div>

                      <button className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                        <i className="ri-play-fill text-lg"></i>
                        立即播放
                      </button>
                    </div>
                  </div>

                  <div className="mt-10">
                    <h2 className="text-lg font-heading font-bold text-foreground-800 mb-4">剧情简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {(movie as typeof movies[0]).description}
                    </p>
                  </div>
                </div>
              </div>
            </article>
          )}

          {!isMovie && (
            <article className="px-4 md:px-6 py-8">
              <div className="max-w-[800px] mx-auto">
                <div className="mb-6">
                  <span className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700">
                    {(article as typeof articles[0]).category}
                  </span>
                  <span className="ml-3 text-xs text-foreground-500">{(article as typeof articles[0]).publishDate}</span>
                </div>

                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-800 leading-tight">
                  {(article as typeof articles[0]).title}
                </h1>

                <div className="flex items-center gap-3 mt-4 text-xs text-foreground-500">
                  <span>作者：{(article as typeof articles[0]).author}</span>
                  <span>阅读：{(article as typeof articles[0]).readCount >= 10000 ? `${((article as typeof articles[0]).readCount / 10000).toFixed(1)}万` : (article as typeof articles[0]).readCount}</span>
                </div>

                <img
                  src={(article as typeof articles[0]).cover}
                  alt={(article as typeof articles[0]).title}
                  className="w-full aspect-[2/1] object-cover object-top rounded-lg mt-6"
                />

                <div
                  className="mt-8 prose prose-sm max-w-none text-foreground-700 leading-relaxed [&>h3]:text-base [&>h3]:font-bold [&>h3]:text-foreground-800 [&>h3]:mt-6 [&>h3]:mb-3 [&>p]:mb-4 [&>p]:text-sm"
                  dangerouslySetInnerHTML={{ __html: (article as typeof articles[0]).content }}
                />

                <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
                  {(article as typeof articles[0]).tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200/70">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          )}

          {relatedMovies.length > 0 && (
            <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <div className="max-w-[1200px] mx-auto">
                <h2 className="text-lg font-heading font-bold text-foreground-800 mb-6">
                  {isMovie ? '相关推荐' : '热门影片推荐'}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4" data-product-shop>
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}